/**
 * Pricing — /pricing (phase 10 §C). Design-system page in the same
 * language as /docs, selling the three tiers from
 * docs/archive/pricing-strategy.md: Solo (free) / Pro $10/mo · $96/yr / Teams
 * $18 per seat/mo · $180/yr with a 3-seat minimum. Tiers meter
 * coordination (machines, agents, schedules, modes, teams) — NEVER
 * tokens: the plane runs zero inference, and needs-human rescue stays
 * free on every tier (both principles render as copy below).
 *
 * CTAs are auth-aware:
 *  - signed-out  → magic-link card (the marketing MagicLinkCard);
 *  - signed-in free → POST /checkout {entitlementId, interval, seats?}
 *    and redirect to the returned Stripe URL;
 *  - entitled (pro/teams/lifetime in /auth/me ownedEntitlements) →
 *    "Your plan" with a lifetime badge where applicable, no buy button.
 * The ?purchase=success|cancelled return query renders a banner.
 */
import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { usePurchaseStatus } from "../hooks/usePurchaseStatus";
import { MagicLinkCard } from "../components/MagicLinkCard";
import { hasLifetime, hasPro, hasTeams } from "../lib/plan";
import { createCheckout, type BillingInterval } from "../lib/api";
import { NSWordmark, SwarmMoon, Icon, I, MonoChip, Btn, Stepper } from "../kit";
import "../marketing.css"; // .mkt-magic — the reused MagicLinkCard
import "../pricing.css";

// Mirrors checkout.ts (MIN_TEAM_SEATS / MAX_SEATS) — the plane validates.
const MIN_TEAM_SEATS = 3;
const MAX_SEATS = 100;

/* ── Top bar (same language as the /docs topbar) ─────────────────── */

function PrcTopbar() {
  return (
    <div className="prc-topbar">
      <div className="prc-container prc-topbar-inner">
        <Link to="/" className="prc-topbar-home" aria-label="nightswarm.app home">
          <NSWordmark size={17} />
        </Link>
        <MonoChip tone="ghost">pricing</MonoChip>
        <div className="prc-topbar-right">
          <Link to="/" className="prc-toplink">
            Home
          </Link>
          <Link to="/docs" className="prc-toplink">
            Docs
          </Link>
          <Link to="/dashboard" className="ns-btn ns-btn--primary ns-btn--sm">
            Open dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ── ?purchase=success|cancelled return banner ───────────────────── */

function PurchaseBanner() {
  const { purchase, dismiss } = usePurchaseStatus();
  if (!purchase) return null;
  const success = purchase === "success";
  return (
    <div className={`prc-banner ${purchase}`} role="status">
      <span className="ic">
        <Icon d={success ? I.check : I.x} size={15} />
      </span>
      <span>
        {success
          ? "Payment complete — your plan is active. Entitlements land within a few seconds of checkout."
          : "Checkout cancelled — nothing was charged."}
      </span>
      <button type="button" className="x" onClick={dismiss} aria-label="Dismiss">
        <Icon d={I.x} size={13} />
      </button>
    </div>
  );
}

/* ── Tier card scaffolding ───────────────────────────────────────── */

function Feature({ children }: { children: ReactNode }) {
  return (
    <li>
      <span className="ic">
        <Icon d={I.check} size={13} />
      </span>
      <span>{children}</span>
    </li>
  );
}

function YourPlan({ lifetime }: { lifetime: boolean }) {
  return (
    <div className="prc-yourplan">
      <MonoChip tone="accent">
        <Icon d={I.check} size={12} /> Your plan
      </MonoChip>
      {lifetime && <MonoChip tone="ghost">lifetime</MonoChip>}
    </div>
  );
}

/* ── Page ────────────────────────────────────────────────────────── */

export default function Pricing() {
  const { status, user } = useAuth();
  const ents = user?.ownedEntitlements ?? [];
  const lifetime = hasLifetime(ents);
  const proOwned = hasPro(ents);
  const teamsOwned = hasTeams(ents);

  // (named to avoid shadowing window.setInterval)
  const [interval, setBillingInterval] = useState<BillingInterval>("monthly");
  const [seats, setSeats] = useState(MIN_TEAM_SEATS);
  const [busy, setBusy] = useState<"pro" | "teams" | null>(null);
  const [buyErr, setBuyErr] = useState<{
    tier: "pro" | "teams";
    message: string;
  } | null>(null);

  const yearly = interval === "yearly";

  const buy = async (entitlementId: "pro" | "teams") => {
    if (busy) return;
    setBusy(entitlementId);
    setBuyErr(null);
    try {
      const { url } = await createCheckout(
        entitlementId,
        interval,
        entitlementId === "teams" ? seats : undefined
      );
      window.location.assign(url); // Stripe-hosted checkout
      // keep `busy` set — we're navigating away
    } catch (e) {
      setBuyErr({
        tier: entitlementId,
        message: e instanceof Error ? e.message : "checkout failed",
      });
      setBusy(null);
    }
  };

  /** Tier CTA per auth state (loading / signed-out / free / entitled). */
  const cta = (tier: "pro" | "teams", owned: boolean, label: string) => {
    if (status === "loading") return <MonoChip tone="ghost">loading…</MonoChip>;
    if (status === "signed-out") {
      return (
        <a className="ns-btn ns-btn--primary" href="#sign-in">
          Sign in to upgrade
        </a>
      );
    }
    if (owned) return <YourPlan lifetime={lifetime} />;
    return (
      <>
        <Btn
          variant="primary"
          disabled={busy !== null}
          onClick={() => buy(tier)}
        >
          {busy === tier ? "Opening checkout…" : label}
        </Btn>
        {buyErr?.tier === tier && (
          <span className="prc-cta-err">{buyErr.message}</span>
        )}
        <span className="prc-cta-note">stripe checkout · cancel anytime</span>
      </>
    );
  };

  const teamsTotal = yearly ? seats * 180 : seats * 18;

  return (
    <div className="prc-page">
      <PrcTopbar />

      <main className="prc-container">
        <PurchaseBanner />

        <header className="prc-hero">
          <div className="prc-kicker">Pricing</div>
          <h1>Pay for coordination. Never for tokens.</h1>
          <p className="prc-lead">
            The thinking runs on Macs you own, through the Claude and ChatGPT
            subscriptions you already pay for. NightSwarm prices the plane —
            machines, agents, schedules, and teams — not the intelligence.
          </p>
        </header>

        {/* monthly ↔ yearly */}
        <div className="prc-toggle-row">
          <div className="prc-toggle" role="tablist" aria-label="Billing interval">
            <button
              type="button"
              role="tab"
              aria-selected={!yearly}
              className={!yearly ? "on" : undefined}
              onClick={() => setBillingInterval("monthly")}
            >
              Monthly
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={yearly}
              className={yearly ? "on" : undefined}
              onClick={() => setBillingInterval("yearly")}
            >
              Yearly
            </button>
          </div>
        </div>

        {/* tiers — copy per docs/archive/pricing-strategy.md */}
        <div className="prc-tiers">
          {/* Solo */}
          <article className="prc-tier">
            <div className="prc-tier-head">
              <h2>Solo</h2>
              <span className="right">
                <MonoChip tone="ghost">free</MonoChip>
              </span>
            </div>
            <p className="prc-tier-tag">
              The whole magic moment — phone → agent on your Mac — free
              forever. Built to beat crontab + tmux, not to tease.
            </p>
            <div className="prc-price">
              <span className="amount">$0</span>
              <span className="per">forever</span>
            </div>
            <div className="prc-price-sub">no card required</div>
            <ul className="prc-feats">
              <Feature>1 machine</Feature>
              <Feature>3 agents</Feature>
              <Feature>5 schedules</Feature>
              <Feature>Claude-app connector + widgets — the full thing</Feature>
              <Feature>Web Push + needs-human approvals</Feature>
              <Feature>Morning review</Feature>
            </ul>
            <div className="prc-cta">
              {status === "loading" ? (
                <MonoChip tone="ghost">loading…</MonoChip>
              ) : status === "signed-out" ? (
                <a className="ns-btn ns-btn--outline" href="#sign-in">
                  Start free
                </a>
              ) : proOwned || teamsOwned ? (
                <MonoChip tone="ghost">included in your plan</MonoChip>
              ) : (
                <YourPlan lifetime={false} />
              )}
            </div>
          </article>

          {/* Pro */}
          <article className="prc-tier featured">
            <div className="prc-tier-head">
              <h2>Pro</h2>
              <span className="right">
                <MonoChip tone="accent">
                  <SwarmMoon size={12} /> night shift
                </MonoChip>
              </span>
            </div>
            <p className="prc-tier-tag">
              The second Mac is the moment the coordination plane starts
              earning. Unlimited swarm, autonomous run modes.
            </p>
            <div className="prc-price">
              <span className="amount">{yearly ? "$96" : "$10"}</span>
              <span className="per">{yearly ? "/yr" : "/mo"}</span>
            </div>
            <div className="prc-price-sub">
              {yearly ? "$8/mo, billed yearly" : "or $96/yr ($8/mo)"}
            </div>
            <ul className="prc-feats">
              <Feature>Everything in Solo</Feature>
              <Feature>Unlimited machines</Feature>
              <Feature>Unlimited agents</Feature>
              <Feature>Unlimited schedules</Feature>
              <Feature>Goal &amp; loop run modes</Feature>
            </ul>
            <div className="prc-cta">
              {cta(
                "pro",
                proOwned,
                yearly ? "Upgrade to Pro — $96/yr" : "Upgrade to Pro — $10/mo"
              )}
            </div>
          </article>

          {/* Teams */}
          <article className="prc-tier">
            <div className="prc-tier-head">
              <h2>Teams</h2>
              <span className="right">
                <MonoChip tone="ghost">per seat</MonoChip>
              </span>
            </div>
            <p className="prc-tier-tag">
              One swarm, shared by the people who review it. We add zero AI
              spend — we make the seats you already bought work nights.
            </p>
            <div className="prc-price">
              <span className="amount">{yearly ? "$180" : "$18"}</span>
              <span className="per">{yearly ? "/seat/yr" : "/seat/mo"}</span>
            </div>
            <div className="prc-price-sub">
              {yearly ? "$15/seat/mo, billed yearly · " : ""}3-seat minimum
            </div>
            <ul className="prc-feats">
              <Feature>Everything in Pro, for every member</Feature>
              <Feature>Shared machines, agents, schedules &amp; queue</Feature>
              <Feature>Morning review with member acks</Feature>
              <Feature>Team-wide needs-human notifications</Feature>
            </ul>
            {!teamsOwned && (
              <div className="prc-seats">
                <span className="lbl">seats</span>
                <Stepper
                  label="seats"
                  value={seats}
                  onDec={() => setSeats((s) => Math.max(MIN_TEAM_SEATS, s - 1))}
                  onInc={() => setSeats((s) => Math.min(MAX_SEATS, s + 1))}
                />
                <span className="total">
                  ${teamsTotal.toLocaleString()}
                  {yearly ? "/yr" : "/mo"}
                </span>
              </div>
            )}
            <div className="prc-cta">
              {cta(
                "teams",
                teamsOwned,
                yearly
                  ? `Get Teams — $${teamsTotal.toLocaleString()}/yr`
                  : `Get Teams — $${teamsTotal.toLocaleString()}/mo`
              )}
            </div>
          </article>
        </div>

        {/* signed-out: the magic-link card the tier CTAs anchor to */}
        {status === "signed-out" && (
          <div className="prc-signin" id="sign-in">
            <p className="h">
              Sign in with a magic link — no password, no credit card.
            </p>
            <MagicLinkCard />
          </div>
        )}

        {/* the two pricing principles */}
        <div className="prc-principles">
          <div className="prc-principle">
            <span className="ic">
              <Icon d={I.zap} size={17} />
            </span>
            <div>
              <h3>We never meter tokens.</h3>
              <p>
                NightSwarm runs zero inference — your agents run on the Claude
                and ChatGPT subscriptions you already own, on machines you own.
                Claude Code and Codex are both supported on every tier, free.
                There is no per-token bill here, on any tier, ever.
              </p>
            </div>
          </div>
          <div className="prc-principle">
            <span className="ic">
              <Icon d={I.bell} size={17} />
            </span>
            <div>
              <h3>Rescue is free, forever.</h3>
              <p>
                Needs-human approvals — the push that says an agent is stuck
                and the approval bar that unsticks it — stay free on every
                tier. Charging to rescue your own agent would be hostage
                pricing.
              </p>
            </div>
          </div>
        </div>
      </main>

      <footer className="prc-footer">
        <div className="prc-container prc-footer-inner">
          <span className="prc-footer-copy">
            © 2026 nightswarm · runs after dark
          </span>
          <nav className="prc-footer-links" aria-label="Footer">
            <Link to="/">Home</Link>
            <Link to="/docs">Docs</Link>
            <Link to="/dashboard">Dashboard</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
